'use client';
import React, { useState } from 'react';
import { Bell, Shield, Globe, ChevronRight, Check } from 'lucide-react';

const settingSections = [
  {
    title: 'Notifications',
    icon: Bell,
    items: [
      { id: 'booking_confirm', label: 'Booking Confirmations', description: 'Get notified when a booking is confirmed', defaultOn: true },
      { id: 'priest_assign', label: 'Priest Assignments', description: 'Notify when a priest is assigned', defaultOn: true },
      { id: 'reminders', label: 'Ritual Reminders', description: '24-hour reminder before your ritual', defaultOn: true },
      { id: 'promotions', label: 'Offers & Promotions', description: 'Receive special offers and discounts', defaultOn: false },
    ],
  },
  {
    title: 'Privacy',
    icon: Shield,
    items: [
      { id: 'profile_visible', label: 'Public Profile', description: 'Allow priests to view your profile', defaultOn: true },
      { id: 'booking_history', label: 'Booking History', description: 'Share booking history with priests', defaultOn: false },
    ],
  },
  {
    title: 'Preferences',
    icon: Globe,
    items: [
      { id: 'dark_mode', label: 'Dark Mode', description: 'Use dark theme across the app', defaultOn: false },
      { id: 'email_updates', label: 'Email Updates', description: 'Receive booking updates via email', defaultOn: true },
    ],
  },
];

export default function Settings() {
  const [toggles, setToggles] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    settingSections.forEach(section => {
      section.items.forEach(item => {
        initial[item.id] = item.defaultOn;
      });
    });
    return initial;
  });
  const [saved, setSaved] = useState(false);

  const toggle = (id: string) => setToggles(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h2 className="font-display text-xl font-bold text-maroon">Settings</h2>
        <p className="text-sm text-maroon/50 mt-0.5">Manage your preferences and account settings</p>
      </div>

      {saved && (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium flex items-center gap-2">
          <Check size={15} /> Settings saved successfully
        </div>
      )}

      {settingSections.map((section) => (
        <div key={section.title} className="bg-white rounded-2xl border border-gold/15 overflow-hidden">
          <div className="px-5 py-4 border-b border-gold/10 flex items-center gap-2">
            <section.icon size={15} className="text-saffron" />
            <h3 className="text-sm font-semibold text-maroon">{section.title}</h3>
          </div>
          <div className="divide-y divide-gold/10">
            {section.items.map((item) => (
              <div key={item.id} className="px-5 py-3.5 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-maroon">{item.label}</p>
                  <p className="text-xs text-maroon/45 mt-0.5">{item.description}</p>
                </div>
                <button
                  onClick={() => toggle(item.id)}
                  className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${
                    toggles[item.id] ? 'bg-saffron' : 'bg-maroon/15'
                  }`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    toggles[item.id] ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Account Actions */}
      <div className="bg-white rounded-2xl border border-gold/15 overflow-hidden">
        <div className="px-5 py-4 border-b border-gold/10">
          <h3 className="text-sm font-semibold text-maroon">Account</h3>
        </div>
        <div className="divide-y divide-gold/10">
          {[
            { label: 'Change Password', desc: 'Update your account password' },
            { label: 'Download My Data', desc: 'Export all your booking data' },
          ].map((item) => (
            <button key={item.label} className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-cream/40 transition-colors text-left">
              <div>
                <p className="text-sm font-medium text-maroon">{item.label}</p>
                <p className="text-xs text-maroon/45 mt-0.5">{item.desc}</p>
              </div>
              <ChevronRight size={16} className="text-maroon/30" />
            </button>
          ))}
          <button className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-red-50 transition-colors text-left group">
            <div>
              <p className="text-sm font-medium text-red-500">Delete Account</p>
              <p className="text-xs text-maroon/45 mt-0.5">Permanently delete your account and data</p>
            </div>
            <ChevronRight size={16} className="text-red-300" />
          </button>
        </div>
      </div>

      <button
        onClick={handleSave}
        className="w-full bg-saffron text-white text-sm font-semibold py-3 rounded-xl hover:bg-saffron/90 transition-colors btn-glow"
      >
        Save Settings
      </button>
    </div>
  );
}
